import React, { useState, useCallback } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import useToastStore from '../store/useToastStore';

const NotificationsPage = () => {
  const {
    permission,
    isSubscribed,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    clearError
  } = usePushNotifications();

  const { showToast } = useToastStore();

  // Derived state for notifications enabled
  const notificationsEnabled = permission === 'granted' && isSubscribed;

  const handleToggleNotifications = useCallback(async () => {
    if (notificationsEnabled) {
      // Disable notifications
      try {
        await unsubscribe();
        showToast('Notifications disabled successfully', 'success');
      } catch (error) {
        showToast('Failed to disable notifications', 'error');
      }
    } else {
      // Enable notifications - first request permission, then subscribe if granted
      try {
        const result = await requestPermission();
        if (result === 'granted') {
          await subscribe();
          showToast('Notifications enabled successfully', 'success');
        } else {
          showToast('Permission denied. Notifications will not work.', 'error');
        }
      } catch (error) {
        showToast('Failed to enable notifications', 'error');
      }
    }
  }, [notificationsEnabled, unsubscribe, subscribe, requestPermission, showToast]);



  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        {/* Main Toggle */}
        <div className="text-center">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Push Notifications
            </h2>
            <p className="text-gray-600">
              {notificationsEnabled
                ? 'Notifications are currently enabled and working'
                : 'Notifications are currently disabled'
              }
            </p>
          </div>

          <button
            onClick={handleToggleNotifications}
            disabled={isLoading}
            className={`px-8 py-3 rounded-lg font-medium transition-colors duration-200 ${notificationsEnabled
              ? 'bg-red-100 text-red-700 hover:bg-red-200 disabled:bg-red-50 disabled:text-red-400'
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:bg-blue-50 disabled:text-blue-400'
              }`}
          >
            {isLoading ? 'Updating...' :
              notificationsEnabled ? 'Disable Notifications' : 'Enable Notifications'}
          </button>
        </div>

        {/* Status Display */}
        <div className="mt-6 text-center">
          {notificationsEnabled && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-800 font-medium">Notifications are enabled and working!</span>
              </div>
            </div>
          )}

          {permission === 'denied' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-900 mb-2">Permission Denied</h4>
              <p className="text-yellow-700 text-sm">
                Notifications are blocked in your browser. To enable them, click the lock/info icon in your browser's address bar and change the notification permission from "Block" to "Allow".
              </p>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
            <div className="flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-red-800 font-medium">Error</span>
            </div>
            <p className="text-red-700 mt-2 text-center">{error}</p>
            <div className="text-center mt-2">
              <button
                onClick={clearError}
                className="text-red-600 hover:text-red-800 text-sm underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage; 